import React from 'react';
import { toast } from 'react-toastify';
import OrderSkeleton from '../order/OrderSkeleton';
import Field from '../order/Field';
import LotsCard from '../order/LotsCard';
import PopupHeader from './PopupHeader';
import useLotsDraft from '../../hooks/useLotsDraft';
import useThunderbirdOrderDetails from '../../hooks/reactQuery/useThunderbirdOrderDetails';
import useThunderbirdOrderUpdate from '../../hooks/reactQuery/useThunderbirdOrderUpdate';
import { formatOrderDateTime } from '../../utils/formatOrderDate';
import formatMoney from '../../utils/formatMoney';

interface ThunderbirdOrderDetailsModalProps {
  orderData: any;
  isLoading: boolean;
  onClose?: () => void;
}

const ThunderbirdOrderDetailsModal: React.FC<
  ThunderbirdOrderDetailsModalProps
> = ({ orderData, isLoading, onClose }) => {
  const { data, isFetching } = useThunderbirdOrderDetails(orderData?.id);
  const { mutate: updateOrder, isLoading: isUpdateLoading } =
    useThunderbirdOrderUpdate(orderData?.id);
  const lots = useLotsDraft(data);

  const handleSaveLots = () => {
    updateOrder(
      { kitLots: lots.kitLots, boxLots: lots.boxLots },
      {
        onSuccess: () => {
          toast.success(`Lots Updated`);
        },
        onError: (error) => {
          toast.error(error?.message || `Failed to update lots`);
        },
      },
    );
  };

  // Header draws from the list row straight away; the body shows a skeleton until the read lands.
  const showSkeleton = isLoading || (isFetching && !data);
  const head = data ?? orderData;

  return (
    <div className="op">
      <PopupHeader
        title={head?.pid || 'Thunderbird order'}
        status={head?.status}
        meta={[
          'Thunderbird order',
          head?.orderDate
            ? `Ordered ${formatOrderDateTime(head.orderDate)}`
            : null,
          data?.total != null ? `Total ${formatMoney(data.total)}` : null,
        ]}
        onClose={onClose}
      />
      <div className="op-body">
        {showSkeleton ? (
          <OrderSkeleton tabs={false} />
        ) : (
          <div className="op-grid" style={{ marginTop: 16 }}>
            <div className="op-col">
              <section className="op-card">
                <div className="op-card-head">
                  <h5>Order</h5>
                </div>
                <div className="op-card-body">
                  <div className="op-fields op-fields--pairs">
                    <Field label="PID">{data?.pid ?? '—'}</Field>
                    <Field label="Order date">
                      {formatOrderDateTime(data?.orderDate) || '—'}
                    </Field>
                    <Field label="Kits">{data?.numberOfKits ?? 0}</Field>
                    <Field label="Kit price">
                      {formatMoney(data?.kitPrice)}
                    </Field>
                    <Field label="Boxes">{data?.numberOfBoxes ?? 0}</Field>
                    <Field label="Box price">
                      {formatMoney(data?.boxPrice)}
                    </Field>
                    <Field label="Total" wide big>
                      {formatMoney(data?.total)}
                    </Field>
                  </div>
                </div>
              </section>
            </div>
            <div className="op-col">
              <LotsCard
                draft={lots}
                kitQuantity={data?.numberOfKits}
                boxQuantity={data?.numberOfBoxes}
                saving={isUpdateLoading}
                onSave={handleSaveLots}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThunderbirdOrderDetailsModal;
